// Firestore's modular Web API on Estra, so ported code is an import swap:
//   import { doc, getDoc, setDoc, … } from "@estra/client/firestore";
// Values map to Estra's wire format: Timestamp/Date ↔ {$t}, GeoPoint ↔ {$geo}, references ↔ {$ref}.
// Not yet: field names containing dots (backtick-quoted paths).
import type { DocOut, Filter, Order, Query as QueryAst, Sentinel, Write } from "@estra/schema";
import { getPath } from "./local.ts";
import { EstraClientError, type Transport } from "./transport.ts";

export type DocumentData = Record<string, any>;
export { EstraClientError as FirestoreError };

const fail = (code: string, msg: string): never => {
  throw new EstraClientError(code, msg);
};

// ---- values -------------------------------------------------------------------------------

export class Timestamp {
  readonly seconds: number;
  readonly nanoseconds: number;
  constructor(seconds: number, nanoseconds: number) {
    this.seconds = seconds;
    this.nanoseconds = nanoseconds;
  }
  static now() {
    return Timestamp.fromMillis(Date.now());
  }
  static fromDate(d: Date) {
    return Timestamp.fromMillis(d.getTime());
  }
  static fromMillis(ms: number) {
    const s = Math.floor(ms / 1000);
    return new Timestamp(s, Math.round((ms - s * 1000) * 1e6));
  }
  toMillis() {
    return this.seconds * 1000 + Math.floor(this.nanoseconds / 1e6);
  }
  toDate() {
    return new Date(this.toMillis());
  }
  isEqual(o: Timestamp) {
    return o instanceof Timestamp && o.seconds === this.seconds && o.nanoseconds === this.nanoseconds;
  }
  valueOf() {
    return String(this.toMillis()).padStart(20, "0");
  }
  toJSON() {
    return { seconds: this.seconds, nanoseconds: this.nanoseconds };
  }
}

export class GeoPoint {
  readonly latitude: number;
  readonly longitude: number;
  constructor(latitude: number, longitude: number) {
    this.latitude = latitude;
    this.longitude = longitude;
  }
  isEqual(o: GeoPoint) {
    return o instanceof GeoPoint && o.latitude === this.latitude && o.longitude === this.longitude;
  }
}

export class FieldValue {
  readonly wire: Sentinel;
  constructor(wire: Sentinel) {
    this.wire = wire;
  }
}
export const serverTimestamp = () => new FieldValue({ $op: "now" });
export const increment = (n: number) => new FieldValue({ $op: "inc", by: n });
export const arrayUnion = (...values: unknown[]) => new FieldValue({ $op: "union", values: values.map(encode) });
export const arrayRemove = (...values: unknown[]) => new FieldValue({ $op: "remove", values: values.map(encode) });
export const deleteField = () => new FieldValue({ $op: "delete" });

export class FieldPath {
  readonly segments: string[];
  constructor(...segments: string[]) {
    this.segments = segments;
  }
}
export const documentId = () => new FieldPath("__name__");

function encode(v: unknown): unknown {
  if (v === undefined) return fail("invalid-argument", "undefined is not a valid value (use null or deleteField())");
  if (v === null || typeof v !== "object") return v;
  if (v instanceof FieldValue) return v.wire;
  if (v instanceof Timestamp) return { $t: v.toMillis() };
  if (v instanceof Date) return { $t: v.getTime() };
  if (v instanceof GeoPoint) return { $geo: [v.latitude, v.longitude] };
  if (v instanceof DocumentReference) return { $ref: v.path };
  if (Array.isArray(v)) return v.map(encode);
  return Object.fromEntries(Object.entries(v as object).map(([k, x]) => [k, encode(x)]));
}

function decode(db: Firestore, v: unknown): any {
  if (v === null || typeof v !== "object") return v;
  if (Array.isArray(v)) return v.map((x) => decode(db, x));
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 1 && typeof o.$t === "number") return Timestamp.fromMillis(o.$t);
  if (keys.length === 1 && Array.isArray(o.$geo)) return new GeoPoint(o.$geo[0] as number, o.$geo[1] as number);
  if (keys.length === 1 && typeof o.$ref === "string") return new DocumentReference(db, o.$ref);
  return Object.fromEntries(keys.map((k) => [k, decode(db, o[k])]));
}

const encodeData = (data: DocumentData): Record<string, unknown> => {
  if (typeof data !== "object" || data === null || Array.isArray(data)) fail("invalid-argument", "data must be an object");
  const out: Record<string, unknown> = {};
  // Dotted keys ("stats.plays") address a sub-field, as in Firestore's updateDoc.
  for (const [k, v] of Object.entries(data)) out[k] = encode(v);
  return out;
};

// ---- references -----------------------------------------------------------------------------

export class Firestore {
  readonly type = "firestore";
  readonly transport: Transport;
  constructor(transport: Transport) {
    this.transport = transport;
  }
}
export const getFirestore = (t: Transport | { transport: Transport }) => new Firestore("transport" in t ? t.transport : t);

const cleanPath = (parts: string[]) => {
  const p = parts.join("/").replace(/^\/+|\/+$/g, "");
  if (!p || p.split("/").some((s) => !s)) fail("invalid-argument", `bad path "${parts.join("/")}"`);
  return p;
};

const AUTO_ID = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
export const autoId = () => Array.from(crypto.getRandomValues(new Uint8Array(20)), (b) => AUTO_ID[b % 62]).join("");

export class DocumentReference<T = DocumentData> {
  readonly type = "document";
  readonly firestore: Firestore;
  readonly path: string;
  declare readonly __t?: T;
  constructor(firestore: Firestore, path: string) {
    this.firestore = firestore;
    this.path = path;
    if (path.split("/").length % 2) fail("invalid-argument", `"${path}" is a collection, not a document`);
  }
  get id() {
    return this.path.split("/").pop()!;
  }
  get parent(): CollectionReference<T> {
    return new CollectionReference<T>(this.firestore, this.path.split("/").slice(0, -1).join("/"));
  }
}

interface QState {
  from?: string;
  group?: string;
  where: Filter[];
  orderBy: Order[];
  limit?: number;
  limitToLast?: number;
  startAfter?: unknown[];
  startAt?: unknown[];
  endAt?: unknown[];
  endBefore?: unknown[];
  near?: { field: string; lat: number; lng: number; radiusMi: number };
}

export class Query<T = DocumentData> {
  readonly type: "query" | "collection" = "query";
  readonly firestore: Firestore;
  readonly state: QState;
  declare readonly __t?: T;
  constructor(firestore: Firestore, state: QState) {
    this.firestore = firestore;
    this.state = state;
  }
  ast(): QueryAst {
    const s = this.state;
    return {
      ...(s.from !== undefined ? { from: s.from } : { group: s.group! }),
      ...(s.where.length ? { where: s.where.length === 1 ? s.where[0]! : { and: s.where } } : {}),
      ...(s.orderBy.length ? { orderBy: s.orderBy } : {}),
      ...(s.limit !== undefined ? { limit: s.limit } : {}),
      ...(s.limitToLast !== undefined ? { limitToLast: s.limitToLast } : {}),
      ...(s.startAfter ? { startAfter: s.startAfter } : {}),
      ...(s.startAt ? { startAt: s.startAt } : {}),
      ...(s.endAt ? { endAt: s.endAt } : {}),
      ...(s.endBefore ? { endBefore: s.endBefore } : {}),
      ...(s.near ? { near: s.near } : {}),
    };
  }
}

export class CollectionReference<T = DocumentData> extends Query<T> {
  override readonly type = "collection";
  readonly path: string;
  constructor(firestore: Firestore, path: string) {
    super(firestore, { from: path, where: [], orderBy: [] });
    this.path = path;
    if (path.split("/").length % 2 === 0) fail("invalid-argument", `"${path}" is a document, not a collection`);
  }
  get id() {
    return this.path.split("/").pop()!;
  }
  get parent(): DocumentReference | null {
    const p = this.path.split("/");
    return p.length > 1 ? new DocumentReference(this.firestore, p.slice(0, -1).join("/")) : null;
  }
}

export function doc(parent: Firestore | CollectionReference<any> | DocumentReference<any>, path?: string, ...more: string[]): DocumentReference {
  if (parent instanceof Firestore) return new DocumentReference(parent, cleanPath([path ?? fail("invalid-argument", "doc() needs a path"), ...more]));
  if (parent instanceof CollectionReference) return new DocumentReference(parent.firestore, cleanPath([parent.path, path ?? autoId(), ...more]));
  return new DocumentReference(parent.firestore, cleanPath([parent.path, path ?? fail("invalid-argument", "doc() needs a path"), ...more]));
}

export function collection(parent: Firestore | DocumentReference<any> | CollectionReference<any>, path: string, ...more: string[]): CollectionReference {
  const db = parent instanceof Firestore ? parent : parent.firestore;
  const base = parent instanceof Firestore ? [] : [parent.path];
  return new CollectionReference(db, cleanPath([...base, path, ...more]));
}

export const collectionGroup = (db: Firestore, id: string) => new Query(db, { group: id, where: [], orderBy: [] });

// ---- constraints ------------------------------------------------------------------------------

type FieldArg = string | FieldPath;
const fieldName = (f: FieldArg) => (f instanceof FieldPath ? f.segments.join(".") : f);

export class QueryConstraint {
  readonly apply: (s: QState) => QState;
  constructor(apply: (s: QState) => QState) {
    this.apply = apply;
  }
}
export class QueryFilterConstraint extends QueryConstraint {
  readonly filter: Filter;
  constructor(filter: Filter) {
    super((s) => ({ ...s, where: [...s.where, filter] }));
    this.filter = filter;
  }
}

export type WhereFilterOp = "<" | "<=" | "==" | "!=" | ">=" | ">" | "array-contains" | "in" | "array-contains-any" | "not-in";

export function where(field: FieldArg, op: WhereFilterOp, value: unknown): QueryFilterConstraint {
  const name = fieldName(field);
  let v = encode(value);
  // documentId() compares ids; accept full paths or references like Firestore does.
  if (name === "__name__") {
    const id = (x: unknown) => (typeof x === "object" && x && "$ref" in x ? String((x as { $ref: string }).$ref) : String(x)).split("/").pop()!;
    v = Array.isArray(v) ? v.map(id) : id(v);
  }
  return new QueryFilterConstraint({ field: name, op, value: v });
}
export const and = (...fs: QueryFilterConstraint[]) => new QueryFilterConstraint({ and: fs.map((f) => f.filter) });
export const or = (...fs: QueryFilterConstraint[]) => new QueryFilterConstraint({ or: fs.map((f) => f.filter) });
export const orderBy = (field: FieldArg, dir: "asc" | "desc" = "asc") => new QueryConstraint((s) => ({ ...s, orderBy: [...s.orderBy, { field: fieldName(field), dir }] }));
// The last limit()/limitToLast() wins, as in Firestore.
export const limit = (n: number) => new QueryConstraint((s) => ({ ...s, limit: n, limitToLast: undefined }));
/** The last n results in orderBy order (needs an orderBy). */
export const limitToLast = (n: number) => new QueryConstraint((s) => ({ ...s, limitToLast: n, limit: undefined }));

function cursorValues(s: QState, args: unknown[]): unknown[] {
  if (args.length === 1 && args[0] instanceof DocumentSnapshot) {
    const snap = args[0];
    const raw = snap._raw ?? fail("invalid-argument", "cursor document doesn't exist");
    return [...s.orderBy.map((o) => (o.field === "__name__" || o.field === "id" ? raw.id : getPath(raw.data, o.field))), raw.id];
  }
  return args.map(encode);
}
export const startAfter = (...args: unknown[]) => new QueryConstraint((s) => ({ ...s, startAfter: cursorValues(s, args), startAt: undefined }));
export const startAt = (...args: unknown[]) => new QueryConstraint((s) => ({ ...s, startAt: cursorValues(s, args), startAfter: undefined }));
/**
 * Radius search (an Estra addition): documents whose geo field is within `radiusMi` of `center`.
 * Without an orderBy, results sort by distance; each snapshot's `distanceMi` says how far. Works
 * in listeners too (moving documents enter and leave the results).
 */
export const near = (field: FieldArg, center: GeoPoint | { lat: number; lng: number }, radiusMi: number) =>
  new QueryConstraint((s) => ({ ...s, near: { field: fieldName(field), lat: center instanceof GeoPoint ? center.latitude : center.lat, lng: center instanceof GeoPoint ? center.longitude : center.lng, radiusMi } }));
export const endAt = (...args: unknown[]) => new QueryConstraint((s) => ({ ...s, endAt: cursorValues(s, args), endBefore: undefined }));
export const endBefore = (...args: unknown[]) => new QueryConstraint((s) => ({ ...s, endBefore: cursorValues(s, args), endAt: undefined }));

export function query<T>(q: Query<T>, ...constraints: QueryConstraint[]): Query<T> {
  return new Query<T>(q.firestore, constraints.reduce((s, c) => c.apply(s), q.state));
}

// ---- snapshots -------------------------------------------------------------------------------

export interface SnapshotMetadata {
  hasPendingWrites: boolean;
  fromCache: boolean;
  isEqual(o: SnapshotMetadata): boolean;
}
const meta = (m?: { fromCache: boolean; hasPendingWrites: boolean }): SnapshotMetadata => {
  const fromCache = !!m?.fromCache, hasPendingWrites = !!m?.hasPendingWrites;
  return { fromCache, hasPendingWrites, isEqual: (o) => o.fromCache === fromCache && o.hasPendingWrites === hasPendingWrites };
};

export class DocumentSnapshot<T = DocumentData> {
  readonly ref: DocumentReference<T>;
  readonly metadata: SnapshotMetadata;
  readonly _raw: DocOut | null;
  constructor(ref: DocumentReference<T>, raw: DocOut | null, m?: { fromCache: boolean; hasPendingWrites: boolean }) {
    this.ref = ref;
    this._raw = raw;
    this.metadata = meta(m ?? raw?.meta);
  }
  get id() {
    return this.ref.id;
  }
  exists(): boolean {
    return this._raw !== null;
  }
  /** For `near` queries: miles from the query's center (an Estra addition). */
  get distanceMi(): number | undefined {
    return this._raw?.distanceMi;
  }
  data(): T | undefined {
    return this._raw ? (decode(this.ref.firestore, this._raw.data) as T) : undefined;
  }
  get(field: FieldArg): any {
    const parts = field instanceof FieldPath ? field.segments : field.split(".");
    let cur: any = this.data();
    for (const p of parts) cur = cur == null ? undefined : cur[p];
    return cur;
  }
}
export class QueryDocumentSnapshot<T = DocumentData> extends DocumentSnapshot<T> {
  override data(): T {
    return super.data()!;
  }
}
export interface DocumentChange<T = DocumentData> {
  type: "added" | "modified" | "removed";
  doc: QueryDocumentSnapshot<T>;
  oldIndex: number;
  newIndex: number;
}

export class QuerySnapshot<T = DocumentData> {
  readonly query: Query<T>;
  readonly docs: QueryDocumentSnapshot<T>[];
  readonly metadata: SnapshotMetadata;
  private readonly changes: DocumentChange<T>[] | null;
  constructor(q: Query<T>, docs: QueryDocumentSnapshot<T>[], changes: DocumentChange<T>[] | null = null, m?: { fromCache: boolean; hasPendingWrites: boolean }) {
    this.query = q;
    this.docs = docs;
    this.changes = changes;
    this.metadata = meta(m ?? { fromCache: docs.some((d) => d.metadata.fromCache), hasPendingWrites: docs.some((d) => d.metadata.hasPendingWrites) });
  }
  /** Changes since the previous snapshot of a listener (all "added" for a one-off read). */
  docChanges(): DocumentChange<T>[] {
    return this.changes ?? this.docs.map((doc, i) => ({ type: "added" as const, doc, oldIndex: -1, newIndex: i }));
  }
  get size() {
    return this.docs.length;
  }
  get empty() {
    return this.docs.length === 0;
  }
  forEach(cb: (d: QueryDocumentSnapshot<T>) => void) {
    this.docs.forEach(cb);
  }
}

// ---- reads ----------------------------------------------------------------------------------

export async function getDoc<T>(ref: DocumentReference<T>): Promise<DocumentSnapshot<T>> {
  const [raw] = await ref.firestore.transport.getAll([ref.path]);
  return new DocumentSnapshot(ref, raw ?? null);
}

/** Without a limit, pages until every match is read (like Firestore), up to 50,000 documents. */
const ALL_CAP = 50_000;
export async function getDocs<T>(q: Query<T>): Promise<QuerySnapshot<T>> {
  const db = q.firestore;
  const ast = q.ast();
  const out: DocOut[] = [];
  let page = await db.transport.query(ast);
  const fromCache = !!page.fromCache;
  out.push(...page.docs);
  while (ast.limit === undefined && ast.limitToLast === undefined && page.hasMore && page.docs.length && out.length < ALL_CAP) {
    const last = page.docs[page.docs.length - 1]!;
    const cursor = [...(ast.orderBy ?? []).map((o) => (o.field === "id" || o.field === "__name__" ? last.id : getPath(last.data, o.field))), last.id];
    page = await db.transport.query({ ...ast, startAfter: cursor, startAt: undefined } as QueryAst);
    out.push(...page.docs);
  }
  const docs = out.map((d) => new QueryDocumentSnapshot(new DocumentReference<T>(db, d.path), d));
  return new QuerySnapshot(q, docs, null, { fromCache, hasPendingWrites: out.some((d) => d.v < 0) });
}

// ---- offline controls (need an offline() transport) -----------------------------------------

type OfflineControls = { waitForPendingWrites(): Promise<void>; disableNetwork(): Promise<void>; enableNetwork(): Promise<void>; clearPersistence(): Promise<void> };
const controls = (db: Firestore) => db.transport as Partial<OfflineControls>;
export const waitForPendingWrites = (db: Firestore) => controls(db).waitForPendingWrites?.() ?? Promise.resolve();
export const disableNetwork = (db: Firestore) => controls(db).disableNetwork?.() ?? fail("unimplemented", "disableNetwork needs offline()");
export const enableNetwork = (db: Firestore) => controls(db).enableNetwork?.() ?? Promise.resolve();
export const clearIndexedDbPersistence = (db: Firestore) => controls(db).clearPersistence?.() ?? Promise.resolve();
export const clearPersistence = clearIndexedDbPersistence;

export async function getCountFromServer(q: Query<any>) {
  const r = await q.firestore.transport.aggregate({ ...q.ast(), aggregate: { count: true } });
  return { data: () => ({ count: Number(r.count ?? 0) }) };
}

type AggSpec = { kind: "count" } | { kind: "sum" | "avg"; field: string };
export const count = (): AggSpec => ({ kind: "count" });
export const sum = (field: string): AggSpec => ({ kind: "sum", field });
export const average = (field: string): AggSpec => ({ kind: "avg", field });
const aggAst = (q: Query<any>, spec: Record<string, AggSpec>): QueryAst => {
  const specs = Object.values(spec);
  return {
    ...q.ast(),
    aggregate: {
      count: specs.some((s) => s.kind === "count"),
      sum: specs.flatMap((s) => (s.kind === "sum" ? [s.field] : [])),
      avg: specs.flatMap((s) => (s.kind === "avg" ? [s.field] : [])),
    },
  };
};
const aggData = (spec: Record<string, AggSpec>, r: Record<string, number | null>) =>
  Object.fromEntries(Object.entries(spec).map(([k, s]) => [k, s.kind === "count" ? Number(r.count ?? 0) : (r[`${s.kind}:${s.field}`] ?? null)]));

export async function getAggregateFromServer(q: Query<any>, spec: Record<string, AggSpec>) {
  const data = aggData(spec, await q.firestore.transport.aggregate(aggAst(q, spec)));
  return { data: () => data };
}

/**
 * Live aggregates (an Estra addition; Firestore has none): calls `next` with the current values,
 * then again whenever a matching document changes them. Same rules and caps as getAggregateFromServer.
 *   onAggregateSnapshot(query(collection(db, "orders"), where("open", "==", true)), { n: count(), total: sum("amount") }, (s) => show(s.data()))
 */
export function onAggregateSnapshot<S extends Record<string, AggSpec>>(q: Query<any>, spec: S, next: (s: { data(): { [K in keyof S]: number | null } }) => void, error?: (e: EstraClientError) => void): Unsubscribe {
  const listen = q.firestore.transport.listen ?? fail("unimplemented", "this transport has no listeners (use httpTransport with a WebSocket)");
  return listen({ query: aggAst(q, spec) }, {
    next: (s) => {
      if (!s.aggregate) return;
      const data = aggData(spec, s.aggregate) as { [K in keyof S]: number | null };
      next({ data: () => data });
    },
    error: (e) => error?.(new EstraClientError(e.code, e.message)),
  });
}

// ---- writes ---------------------------------------------------------------------------------

export interface SetOptions {
  merge?: boolean;
}

const setWrite = (ref: DocumentReference<any>, data: DocumentData, o?: SetOptions): Write => ({ op: "set", path: ref.path, data: encodeData(data), ...(o?.merge ? { merge: true } : {}) });
function updateWrite(ref: DocumentReference<any>, a: DocumentData | FieldArg, rest: unknown[]): Write {
  const data: DocumentData = typeof a === "string" || a instanceof FieldPath ? {} : a;
  if (typeof a === "string" || a instanceof FieldPath) {
    const pairs = [a, ...rest];
    for (let i = 0; i < pairs.length; i += 2) data[fieldName(pairs[i] as FieldArg)] = pairs[i + 1];
  }
  return { op: "update", path: ref.path, data: encodeData(data) };
}

const commitOne = async (db: Firestore, w: Write) => {
  await db.transport.commit({ writes: [w] });
};
/**
 * Hot counters: adds `by` to a number field through the server's counter buffer (one commit about
 * every second, however many calls). Resolves with the new value (stored + buffered). Needs
 * `createEstra({ counters })`, and passes the document's update rule per call. Online only.
 */
export async function incrementBuffered(ref: DocumentReference<any>, field: string | FieldPath, by = 1): Promise<number | null> {
  const inc = ref.firestore.transport.counterInc ?? fail("unimplemented", "this transport has no counters (use httpTransport)");
  return (await inc(ref.path, fieldName(field), by)).value;
}

export const setDoc = (ref: DocumentReference<any>, data: DocumentData, o?: SetOptions) => commitOne(ref.firestore, setWrite(ref, data, o));
export const updateDoc = (ref: DocumentReference<any>, a: DocumentData | FieldArg, ...rest: unknown[]) => commitOne(ref.firestore, updateWrite(ref, a, rest));
export const deleteDoc = (ref: DocumentReference<any>) => commitOne(ref.firestore, { op: "delete", path: ref.path });
export async function addDoc<T>(coll: CollectionReference<T>, data: DocumentData): Promise<DocumentReference<T>> {
  const ref = doc(coll) as DocumentReference<T>;
  await coll.firestore.transport.commit({ writes: [{ ...setWrite(ref, data), pre: { exists: false } }] });
  return ref;
}

export class WriteBatch {
  readonly db: Firestore;
  readonly writes: Write[] = [];
  constructor(db: Firestore) {
    this.db = db;
  }
  set(ref: DocumentReference<any>, data: DocumentData, o?: SetOptions) {
    this.writes.push(setWrite(ref, data, o));
    return this;
  }
  update(ref: DocumentReference<any>, a: DocumentData | FieldArg, ...rest: unknown[]) {
    this.writes.push(updateWrite(ref, a, rest));
    return this;
  }
  delete(ref: DocumentReference<any>) {
    this.writes.push({ op: "delete", path: ref.path });
    return this;
  }
  async commit() {
    if (this.writes.length) await this.db.transport.commit({ writes: this.writes });
  }
}
export const writeBatch = (db: Firestore) => new WriteBatch(db);

export class Transaction {
  readonly db: Firestore;
  readonly reads = new Map<string, number>();
  readonly writes: Write[] = [];
  constructor(db: Firestore) {
    this.db = db;
  }
  async get<T>(ref: DocumentReference<T>): Promise<DocumentSnapshot<T>> {
    if (this.writes.length) fail("invalid-argument", "transactions must read before they write");
    const snap = await getDoc(ref);
    this.reads.set(ref.path, snap._raw?.v ?? 0);
    return snap;
  }
  set(ref: DocumentReference<any>, data: DocumentData, o?: SetOptions) {
    this.writes.push(setWrite(ref, data, o));
    return this;
  }
  update(ref: DocumentReference<any>, a: DocumentData | FieldArg, ...rest: unknown[]) {
    this.writes.push(updateWrite(ref, a, rest));
    return this;
  }
  delete(ref: DocumentReference<any>) {
    this.writes.push({ op: "delete", path: ref.path });
    return this;
  }
}

/** Reads, then writes guarded on the versions read; retried when a read went stale. */
export async function runTransaction<R>(db: Firestore, fn: (t: Transaction) => Promise<R>, o: { maxAttempts?: number } = {}): Promise<R> {
  const max = o.maxAttempts ?? 5;
  for (let attempt = 1; ; attempt++) {
    const t = new Transaction(db);
    const result = await fn(t);
    const writes: Write[] = t.writes.map((w) => (t.reads.has(w.path) ? { ...w, pre: { ...w.pre, version: t.reads.get(w.path)! } } : w));
    // Documents read but not written are guarded too: the commit fails if any of them changed.
    const written = new Set(t.writes.map((w) => w.path));
    for (const [path, version] of t.reads) if (!written.has(path)) writes.push({ op: "check", path, pre: { version } });
    try {
      if (t.writes.length) await db.transport.commit({ writes });
      return result;
    } catch (e) {
      const stale = e instanceof EstraClientError && (e.code === "failed-precondition" || e.code === "aborted");
      if (!stale || attempt >= max) throw e;
    }
  }
}

type Observer<S> = { next?: (s: S) => void; error?: (e: EstraClientError) => void; complete?: () => void };
export type Unsubscribe = () => void;

export function onSnapshot<T>(ref: DocumentReference<T>, next: ((s: DocumentSnapshot<T>) => void) | Observer<DocumentSnapshot<T>>, error?: (e: EstraClientError) => void): Unsubscribe;
export function onSnapshot<T>(q: Query<T>, next: ((s: QuerySnapshot<T>) => void) | Observer<QuerySnapshot<T>>, error?: (e: EstraClientError) => void): Unsubscribe;
export function onSnapshot<T>(ref: DocumentReference<T>, options: { includeMetadataChanges?: boolean }, next: (s: DocumentSnapshot<T>) => void, error?: (e: EstraClientError) => void): Unsubscribe;
export function onSnapshot<T>(q: Query<T>, options: { includeMetadataChanges?: boolean }, next: (s: QuerySnapshot<T>) => void, error?: (e: EstraClientError) => void): Unsubscribe;
export function onSnapshot(target: DocumentReference<any> | Query<any>, ...args: any[]): Unsubscribe {
  let includeMeta = false;
  if (args[0] && typeof args[0] === "object" && !("next" in args[0]) && !("error" in args[0])) includeMeta = !!args.shift().includeMetadataChanges;
  const obs: Observer<any> = typeof args[0] === "function" ? { next: args[0], error: args[1] } : (args[0] ?? {});
  const db = target.firestore;
  const listen = db.transport.listen ?? fail("unimplemented", "this transport has no listeners (use httpTransport with a WebSocket)");
  const onError = (e: { code: string; message: string }) => obs.error?.(new EstraClientError(e.code, e.message));

  // Like Firestore, snapshots whose only change is metadata (fromCache, hasPendingWrites) are
  // skipped unless includeMetadataChanges is set.
  const metaKey = (m?: { fromCache?: boolean; hasPendingWrites?: boolean }) => `${!!m?.fromCache}|${!!m?.hasPendingWrites}`;
  const sameData = (a: DocOut | null | undefined, b: DocOut | null | undefined) => (!a && !b) || (!!a && !!b && (a.v === b.v || JSON.stringify(a.data) === JSON.stringify(b.data)));

  if (target instanceof DocumentReference) {
    let cur: DocOut | null = null;
    let first = true;
    let lastMeta = "";
    return listen({ doc: target.path }, {
      next: (s) => {
        const prev = cur;
        cur = s.docs[0] ?? (s.removed?.length || s.reset ? null : cur);
        const meta = metaKey(s.meta);
        const quiet = !first && sameData(prev, cur) && (!includeMeta || meta === lastMeta);
        first = false;
        lastMeta = meta;
        if (!quiet) obs.next?.(new DocumentSnapshot(target, cur, s.meta));
      },
      error: onError,
      resume: () => (first ? undefined : cur ? { [cur.path]: cur.v } : {}),
    });
  }

  const q = target as Query<any>;
  const cache = new Map<string, DocOut>();
  let order: string[] = [];
  let first = true;
  let lastMeta = "";
  return listen({ query: q.ast() }, {
    resume: () => (first ? undefined : Object.fromEntries([...cache.values()].filter((d) => d.v > 0).map((d) => [d.path, d.v]))),
    next: (s) => {
      const prevOrder = order;
      const prevDocs = new Map(prevOrder.map((p) => [p, cache.get(p)]));
      if (s.reset) cache.clear();
      for (const p of s.removed ?? []) cache.delete(p);
      for (const d of s.docs) cache.set(d.path, d);
      order = s.order ?? order;
      const snap = (p: string) => new QueryDocumentSnapshot(new DocumentReference(db, p), cache.get(p)!);
      const docs = order.filter((p) => cache.has(p)).map(snap);
      const changes: DocumentChange[] = [];
      if (!first) {
        const now = new Set(order);
        prevOrder.forEach((p, i) => {
          if (!now.has(p)) changes.push({ type: "removed", doc: new QueryDocumentSnapshot(new DocumentReference(db, p), { path: p, id: p.split("/").pop()!, v: 0, createTime: 0, updateTime: 0, data: {} }), oldIndex: i, newIndex: -1 });
        });
        order.forEach((p, i) => {
          const was = prevOrder.indexOf(p);
          if (was < 0) changes.push({ type: "added", doc: snap(p), oldIndex: -1, newIndex: i });
          else {
            const before = prevDocs.get(p);
            const after = cache.get(p);
            // A data change, or (with includeMetadataChanges) a pending write that got confirmed.
            const metaOnly = sameData(before, after) && before?.v !== after?.v && (before?.v ?? 0) < 0 !== (after?.v ?? 0) < 0;
            if (!sameData(before, after) || (includeMeta && metaOnly)) changes.push({ type: "modified", doc: snap(p), oldIndex: was, newIndex: i });
          }
        });
        for (const p of prevOrder) if (!new Set(order).has(p)) cache.delete(p);
      }
      const meta = metaKey(s.meta);
      const moved = prevOrder.join("\n") !== order.join("\n");
      const quiet = !first && !changes.length && !moved && (!includeMeta || meta === lastMeta);
      const out = new QuerySnapshot(q, docs, first ? null : changes, s.meta);
      first = false;
      lastMeta = meta;
      if (!quiet) obs.next?.(out);
    },
    error: onError,
  });
}
